/**
 * The life map, normalised — one shape both renderers read.
 *
 * The backend sends a raw union of every dream-rooted tree plus semantic and
 * place edges, which is not directly drawable: the same relationship arrives
 * twice under inverse names, an "adopted" habit reaches its goal through
 * `related_to` rather than a structural edge, and a journey's locked steps
 * have no entity to point at yet. This module turns that payload into a set
 * of keyed nodes, a tree skeleton, and a separate list of cross-links.
 *
 * It is deliberately free of React and of any drawing concern so that the
 * ReactFlow canvas and the SVG diagram cannot drift apart on the questions
 * that actually confuse users — which parent a node hangs from, and whether a
 * link is drawn once or twice.
 */
import type {
  DreamGraphPayload,
  GoalJourney,
  GraphEdge,
  GraphNode,
} from '../../services/hierarchyGraphService'

/**
 * Relationships that behave like parent→child for layout purposes. An adopted
 * habit (linked to a goal via `related_to`) should sit in the row with the
 * goal's canonical children, not float off to the side on a cross-link.
 */
export const CHILD_LIKE_RELS = new Set<string>([
  'supports',
  'supported_by',
  'involves',
  'related_to',
])

/** Relative depth, used to orient child-like edges parent-first. */
export const TYPE_DEPTH: Record<string, number> = {
  dream: 0,
  aspiration: 1,
  goal: 2,
  skill: 3,
  habit: 4,
  task: 5,
  action_item: 6,
  roadblock: 7,
  person: 8,
  place: 9,
}

const INVERSE_REL: Record<string, string> = {
  supported_by: 'supports',
  blocked_by: 'blocks',
  part_of: 'part_of',
}

export interface ModelNode {
  /** Stable key: `${type}-${id}` for real nodes, `ghost-…` for locked steps. */
  key: string
  node: GraphNode
  isGhost: boolean
  /** The parent this node hangs from in THIS graph, or null for a root. */
  parentKey: string | null
}

export interface ModelEdge {
  id: string
  sourceKey: string
  targetKey: string
  kind: 'structural' | 'semantic'
  relationship: string
}

export interface GraphModel {
  nodes: ModelNode[]
  byKey: Map<string, ModelNode>
  /** The tree skeleton: exactly what the layout engine ranks on. */
  structural: ModelEdge[]
  /** Everything else — blocks, derived-from, location — drawn over the tree. */
  cross: ModelEdge[]
}

export const nodeKey = (n: { type: string; id: number }): string =>
  `${n.type}-${n.id}`

/** Unordered pair key, so "A→B" and "B→A" land in the same bucket. */
const pairKey = (a: string, b: string): string => (a < b ? `${a}|${b}` : `${b}|${a}`)

const canonRel = (r: string): string => INVERSE_REL[r] ?? r

const depthOf = (type: string): number => TYPE_DEPTH[type] ?? 99

/**
 * Narrow a payload to one kind of thing, keeping each match's ancestor chain
 * so the tree stays readable rather than becoming a row of orphans.
 *
 * `'world'` means people and places. When the user picks a kind they own none
 * of, the filter is ignored on purpose — an empty map reads as data loss.
 */
export function filterPayloadByKind(
  payload: DreamGraphPayload,
  kindFilter: string | null | undefined,
): DreamGraphPayload {
  if (!kindFilter) return payload
  const want =
    kindFilter === 'world' ? new Set(['person', 'place']) : new Set([kindFilter])

  const byKey = new Map<string, GraphNode>()
  for (const n of payload.nodes) byKey.set(nodeKey(n), n)

  const keep = new Set(
    payload.nodes.filter((n) => want.has(n.type)).map((n) => nodeKey(n)),
  )
  if (keep.size === 0) return payload

  // Walk child-like edges upward until no new ancestor joins.
  let grew = true
  while (grew) {
    grew = false
    for (const e of payload.edges) {
      if (e.kind !== 'structural' && !CHILD_LIKE_RELS.has(e.relationship)) continue
      const s = byKey.get(e.source)
      const t = byKey.get(e.target)
      if (!s || !t) continue
      const parent = depthOf(s.type) <= depthOf(t.type) ? e.source : e.target
      const child = depthOf(s.type) <= depthOf(t.type) ? e.target : e.source
      if (keep.has(child) && !keep.has(parent)) {
        keep.add(parent)
        grew = true
      }
    }
  }

  return {
    ...payload,
    nodes: payload.nodes.filter((n) => keep.has(nodeKey(n))),
    edges: payload.edges.filter((e) => keep.has(e.source) && keep.has(e.target)),
  }
}

/**
 * Payload → drawable model.
 *
 * Order of operations matters: dedupe first (so a promoted edge cannot
 * resurrect a duplicate), then promote child-like semantic edges into the
 * skeleton, then collapse any pair that ended up with both a real structural
 * edge and a promoted one, and only then synthesise ghost steps.
 */
export function buildGraphModel(payload: DreamGraphPayload): GraphModel {
  const rawByKey = new Map<string, GraphNode>()
  for (const n of payload.nodes) rawByKey.set(nodeKey(n), n)

  const seen = new Set<string>()
  const deduped: GraphEdge[] = []
  for (const e of payload.edges) {
    if (!rawByKey.has(e.source) || !rawByKey.has(e.target)) continue
    if (e.source === e.target) continue
    const k = `${pairKey(e.source, e.target)}|${canonRel(e.relationship)}`
    if (seen.has(k)) continue
    seen.add(k)
    deduped.push(e)
  }

  const promoted: GraphEdge[] = deduped.map((e) => {
    if (e.kind !== 'semantic' || !CHILD_LIKE_RELS.has(e.relationship)) return e
    const s = rawByKey.get(e.source)
    const t = rawByKey.get(e.target)
    const flip = !!(s && t && depthOf(s.type) > depthOf(t.type))
    return {
      source: flip ? e.target : e.source,
      target: flip ? e.source : e.target,
      kind: 'structural',
      relationship: 'part_of',
    }
  })

  const structuralPairs = new Set<string>()
  const structural: ModelEdge[] = []
  const cross: ModelEdge[] = []

  promoted.forEach((e, idx) => {
    if (e.kind !== 'structural') return
    const k = pairKey(e.source, e.target)
    if (structuralPairs.has(k)) return
    structuralPairs.add(k)
    structural.push({
      id: `s-${e.source}-${e.target}-${e.relationship}-${idx}`,
      sourceKey: e.source,
      targetKey: e.target,
      kind: 'structural',
      relationship: e.relationship,
    })
  })
  promoted.forEach((e, idx) => {
    if (e.kind !== 'semantic') return
    if (structuralPairs.has(pairKey(e.source, e.target))) return
    cross.push({
      id: `x-${e.source}-${e.target}-${e.relationship}-${idx}`,
      sourceKey: e.source,
      targetKey: e.target,
      kind: 'semantic',
      relationship: e.relationship,
    })
  })

  // Locked journey steps whose entity does not exist yet, anchored under the
  // goal so they lay out beside the real children instead of nowhere.
  const ghosts: ModelNode[] = []
  const journeys: GoalJourney[] = payload.journeys ?? []
  for (const j of journeys) {
    const goalKey = `goal-${j.goal_id}`
    if (!rawByKey.has(goalKey)) continue
    for (const step of j.steps) {
      if (step.status !== 'locked' || step.entity_id) continue
      const key = `ghost-${j.plan_id ?? j.goal_id}-${step.step_id}`
      ghosts.push({
        key,
        isGhost: true,
        parentKey: goalKey,
        node: {
          id: 0,
          type: step.child_type,
          title: step.title || '(next step)',
          status: null,
          is_current: false,
          primary_parent: null,
          isGhost: true,
          ghostStep: step,
        },
      })
      structural.push({
        id: `s-${goalKey}-${key}`,
        sourceKey: goalKey,
        targetKey: key,
        kind: 'structural',
        relationship: 'planned',
      })
    }
  }

  const parentOf = new Map<string, string>()
  for (const e of structural) {
    if (!parentOf.has(e.targetKey)) parentOf.set(e.targetKey, e.sourceKey)
  }

  const nodes: ModelNode[] = [
    ...payload.nodes.map((n) => {
      const key = nodeKey(n)
      return { key, node: n, isGhost: false, parentKey: parentOf.get(key) ?? null }
    }),
    ...ghosts.map((g) => ({ ...g, parentKey: parentOf.get(g.key) ?? g.parentKey })),
  ]

  const byKey = new Map(nodes.map((n) => [n.key, n]))

  return {
    nodes,
    byKey,
    structural: structural.filter(
      (e) => byKey.has(e.sourceKey) && byKey.has(e.targetKey),
    ),
    cross: cross.filter((e) => byKey.has(e.sourceKey) && byKey.has(e.targetKey)),
  }
}